import { useState } from "react";
import clsx from "clsx";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faShieldHalved, faSpinner } from "@awesome.me/kit-70c1f0055f/icons/classic/regular";
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogTitle,
} from "@/components/ui/dialog/Dialog";
import { Button } from "@/components/ui/button/Button";
import { EntitySearch } from "@/components/ui/entity-search";
import { useNotification } from "@/context/NotificationContext";
import {
  NEW_AUDIT_SEARCH_KINDS,
  auditableEntityToQueueRef,
  queueAudits,
  type AuditableEntityRow,
  type AuditLaunchRef,
} from "@/services/api/guardiansv2";
import { guardiansToolbarSlotButtonClass, GUARDIANS_TOOLBAR_ACTION_MIN_WIDTH_PX } from "../shared/guardiansToolbarButton";
import { CompressibleToolbarLabel } from "../shared/CompressibleToolbarLabel";

export interface NewAuditButtonProps {
  onClick: () => void;
}

/** Opens the new-audit dialog on the Audits tab. */
export function NewAuditButton({ onClick }: NewAuditButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      title="New audit"
      data-toolbar-slot-button
      style={{ minWidth: GUARDIANS_TOOLBAR_ACTION_MIN_WIDTH_PX }}
      className={clsx(
        guardiansToolbarSlotButtonClass,
        "bg-indigo-600 text-white hover:bg-indigo-500"
      )}
      data-testid="guardians-new-audit-button"
    >
      <FontAwesomeIcon icon={faPlus} className="shrink-0" />
      <CompressibleToolbarLabel label="New audit" className="min-w-0" />
    </button>
  );
}

export interface NewAuditDialogProps {
  open: boolean;
  onClose: () => void;
  onQueued?: (launches: AuditLaunchRef[]) => void;
}

/** Modal to search and queue Guardians audits for multiple entities. */
export function NewAuditDialog({ open, onClose, onQueued }: NewAuditDialogProps) {
  const { showSuccess, showError } = useNotification();
  const [selected, setSelected] = useState<AuditableEntityRow[]>([]);
  const [submitting, setSubmitting] = useState(false);

  const handleClose = () => {
    if (submitting) return;
    setSelected([]);
    onClose();
  };

  const handleQueue = async () => {
    if (selected.length === 0 || submitting) return;
    setSubmitting(true);
    try {
      const result = await queueAudits(selected.map(auditableEntityToQueueRef));
      if (result.queued > 0) {
        showSuccess(
          result.queued === 1
            ? "Guardians audit queued"
            : `${result.queued} Guardians audits queued`
        );
        onQueued?.(result.launches ?? []);
        handleClose();
      }
      if (result.failed.length > 0) {
        showError(
          result.failed.length === 1
            ? result.failed[0].reason
            : `${result.failed.length} entities could not be queued`
        );
      }
    } catch (err) {
      showError(err instanceof Error ? err.message : "Could not queue audits");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} size="lg">
      <DialogTitle>
        <span className="flex items-center gap-2">
          <FontAwesomeIcon icon={faShieldHalved} className="text-indigo-400" />
          New Guardians audit
        </span>
      </DialogTitle>
      <DialogBody>
        <p className="mb-4 text-sm text-slate-400">
          Search for missions, tasks, worker interactions, artefacts, and more. Paste an
          interaction or conversation ObjectId from Workers to audit a chat turn.
        </p>
        <EntitySearch
          kinds={[...NEW_AUDIT_SEARCH_KINDS]}
          multi
          selected={selected}
          onChange={setSelected}
          placeholder="Search missions, tasks, interactions, artefacts…"
          debounceMs={1000}
          minQueryLength={3}
        />
      </DialogBody>
      <DialogActions>
        <Button type="button" outline disabled={submitting} onClick={handleClose}>
          Cancel
        </Button>
        <Button
          type="button"
          disabled={submitting || selected.length === 0}
          onClick={() => void handleQueue()}
        >
          {submitting ? (
            <span className="inline-flex items-center gap-2">
              <FontAwesomeIcon icon={faSpinner} className="h-4 w-4 animate-spin" />
              {selected.length <= 1 ? "Queue audit" : `Queue ${selected.length} audits`}
            </span>
          ) : selected.length <= 1 ? (
            "Queue audit"
          ) : (
            `Queue ${selected.length} audits`
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
